import React from 'react';
import { motion } from 'framer-motion';
import Web3WalletConnect from '../components/web3/Web3WalletConnect';
import MainLayout from '../layouts/MainLayout';

const Web3ConnectPage = () => {
  const handleWalletConnected = (walletData) => {
    console.log('Wallet connected:', walletData);
    // You can store wallet data in localStorage or state management
    localStorage.setItem('walletData', JSON.stringify(walletData));
  };

  const handleApplicationSubmit = (result) => {
    console.log('Application submitted:', result);
    // Handle successful application submission
    // You can redirect to a success page or show a success message
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="pt-20 pb-16 text-center"
        >
          <div className="max-w-4xl mx-auto px-6">
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Web3 Authentication
            </h1>
            <p className="text-xl lg:text-2xl text-gray-600 mb-8">
              Connect your wallet to authenticate and submit your investment application
            </p>
            <div className="flex items-center justify-center gap-4 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                Secure
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                Decentralized
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                Instant
              </div>
            </div>
          </div>
        </motion.div>

        {/* Wallet Connection Component */}
        <Web3WalletConnect
          onWalletConnected={handleWalletConnected}
          onApplicationSubmit={handleApplicationSubmit}
        />

        {/* Features Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="py-20"
        >
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 text-center mb-16">
              Why Choose Web3 Authentication?
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Enhanced Security</h3>
                <p className="text-gray-600">
                  Your private keys never leave your device. Only you have control over your authentication.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Instant Verification</h3>
                <p className="text-gray-600">
                  No waiting for email verification or manual processing. Connect and apply instantly.
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Trustless Process</h3>
                <p className="text-gray-600">
                  Built on blockchain technology ensuring transparency and immutability of your application.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Supported Wallets */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="py-20 bg-white"
        >
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 text-center mb-16">
              Supported Wallets
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-orange-600">M</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">MetaMask</h3>
                <p className="text-sm text-gray-600">Most popular</p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-blue-600">W</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">WalletConnect</h3>
                <p className="text-sm text-gray-600">Mobile friendly</p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-purple-600">C</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Coinbase</h3>
                <p className="text-sm text-gray-600">Exchange wallet</p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-amber-600">T</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Trust Wallet</h3>
                <p className="text-sm text-gray-600">Binance ecosystem</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </MainLayout>
  );
};

export default Web3ConnectPage;
